import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "../../i18n/LanguageContext";
import { LanguageSwitcher } from "./LanguageSwitcher";

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Détecte le scroll
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
    };

    onScroll();

    window.addEventListener("scroll", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Ferme le menu mobile lors d'un changement de page
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Bloque le scroll de la page lorsque le menu mobile est ouvert
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const isHome = location.pathname === "/";

  // Header transparent uniquement en haut de la page d'accueil
  const transparent = isHome && !scrolled && !open;

  const nav = [
    { to: "/", label: t("nav.home") },
    { to: "/about", label: t("nav.about") },
    { to: "/ministries", label: t("nav.ministries") },
    { to: "/events", label: t("nav.events") },
    { to: "/teachings", label: t("nav.media") },
    { to: "/impact", label: t("nav.impact") },
    { to: "/churches", label: t("nav.churches") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        transparent
          ? "bg-transparent"
          : "bg-dmi-charcoal/85 backdrop-blur-xl border-b border-white/5"
      }`}
      data-testid="site-header"
    >
      <div className="dmi-container flex items-center justify-between h-20 lg:h-24">

        {/* Logo */}
        <Link
          to="/"
          className="flex items-center relative z-[60]"
          data-testid="logo-link"
        >
          <img
            src="/images/dmi_logo_update-removebg-preview.png"
            alt="Dalo Ministries International"
            className="h-16 w-auto object-contain transition-transform duration-300"
          />
        </Link>

        {/* Navigation desktop */}
        <nav
          className="hidden xl:flex items-center gap-8"
          data-testid="desktop-nav"
        >
          {nav.slice(0, 7).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-[13px] tracking-wide transition-colors underline-grow ${
                  isActive
                    ? "text-dmi-gold"
                    : "text-white/80 hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Actions desktop */}
        <div className="hidden xl:flex items-center gap-5">
          <LanguageSwitcher tone="light" />

          <Link
            to="/member"
            className="text-[12px] tracking-wide text-white/80 hover:text-white transition-colors"
            data-testid="header-member-btn"
          >
            {t("nav.member")}
          </Link>

          <Link
            to="/partnership"
            className="btn-gold !py-2.5 !px-5 text-[12px]"
            data-testid="header-donate-btn"
          >
            {t("cta.donate")}
          </Link>
        </div>

        {/* Bouton menu mobile / tablette */}
        <button
          type="button"
          className="xl:hidden relative z-[60] text-white"
          onClick={() => setOpen((value) => !value)}
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={open}
          aria-controls="mobile-navigation"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Menu mobile / tablette */}
      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-navigation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="
              xl:hidden
              fixed
              inset-x-0
              top-20
              lg:top-24
              bottom-0
              z-[55]
              bg-dmi-charcoal
              flex
              flex-col
            "
            data-testid="mobile-menu"
          >
            <div className="flex-1 overflow-y-auto px-6 py-8">

              {/* Liens principaux */}
              <div className="flex flex-col gap-1">
                {nav.map((item, index) => (
                  <motion.div
                    key={item.to}
                    initial={{
                      opacity: 0,
                      x: -20,
                    }}
                    animate={{
                      opacity: 1,
                      x: 0,
                    }}
                    transition={{
                      delay: 0.05 * index,
                    }}
                  >
                    <NavLink
                      to={item.to}
                      end={item.to === "/"}
                      className={({ isActive }) =>
                        `block py-4 font-serif text-3xl border-b border-white/5 ${
                          isActive
                            ? "text-dmi-gold"
                            : "text-white"
                        }`
                      }
                      data-testid={`mobile-nav-${
                        item.to.replace("/", "") || "home"
                      }`}
                    >
                      {item.label}
                    </NavLink>
                  </motion.div>
                ))}

                {/* Prière */}
                <NavLink
                  to="/prayer"
                  className={({ isActive }) =>
                    `block py-4 font-serif text-3xl border-b border-white/5 ${
                      isActive
                        ? "text-dmi-gold"
                        : "text-white"
                    }`
                  }
                  data-testid="mobile-nav-prayer"
                >
                  {t("nav.prayer")}
                </NavLink>

                {/* Devenir membre */}
                <NavLink
                  to="/member"
                  className={({ isActive }) =>
                    `block py-4 font-serif text-3xl border-b border-white/5 ${
                      isActive
                        ? "text-dmi-gold"
                        : "text-white"
                    }`
                  }
                  data-testid="mobile-nav-member"
                >
                  {t("nav.member")}
                </NavLink>
              </div>

              {/* Actions */}
              <div className="mt-10 flex flex-col gap-4">

                <Link
                  to="/partnership"
                  className="btn-gold w-full"
                  data-testid="mobile-donate-btn"
                >
                  {t("cta.donate")}
                </Link>

                <div className="pt-3">
                  <LanguageSwitcher tone="light" />
                </div>

              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};